import { LocationInterface } from '@fc-locations/models/location-interface';
import { FilterItem } from '@fc-vehicles/components/shared/vehicle-filter/vehicles-filter.component';
import { SortBy } from '@fc-shared/ui/sort-by/sort-by';
import circle from '@turf/circle';
import moment from 'moment-timezone';
import { getLocationArea } from '@fc-shared/utils/data.utils';
import { sortByPropertyWithOrder } from '@fc-shared/utils/sort-by-property';

export const LOCATION_GROUP_FILTER_TYPE = 'group';
export const LOCATION_ACTIVITY_FILTER_TYPE = 'activity';

export interface LocationGroupFilterItem extends FilterItem {
  id: number;
  type: typeof LOCATION_GROUP_FILTER_TYPE;
}

export interface LocationActivityFilterItem extends FilterItem {
  id: boolean;
  type: typeof LOCATION_ACTIVITY_FILTER_TYPE;
}

const isLocationGroupFilterItem = (
  filter: FilterItem,
): filter is LocationGroupFilterItem =>
  filter.type === LOCATION_GROUP_FILTER_TYPE;

export const isLocationActivityFilterItem = (
  filter: FilterItem,
): filter is LocationActivityFilterItem =>
  filter.type === LOCATION_ACTIVITY_FILTER_TYPE;

export const countVehiclesInLocation = (location: LocationInterface): number =>
  location.vehiclesTypesTotal?.reduce(
    (sum, vehicleType) => sum + vehicleType.total,
    0,
  ) ?? 0;

export const applySelectedLocationFilters = (
  locations: LocationInterface[],
  filters: FilterItem[],
): LocationInterface[] => {
  if (!filters?.length) return locations;
  const groupFilters = filters.filter(isLocationGroupFilterItem);
  if (groupFilters.length) {
    const filteredGroupIds = new Set(groupFilters.map((filter) => filter.id));
    locations = locations.filter((location) =>
      location.groups?.some((group) => filteredGroupIds.has(group.id)),
    );
  }
  const activityFilter = filters.find(isLocationActivityFilterItem);
  if (activityFilter) {
    locations = locations.filter(
      (location) => countVehiclesInLocation(location) > 0 === activityFilter.id,
    );
  }
  return locations;
};

export const locationsSortBy = (
  locations: LocationInterface[],
  sortBy: SortBy,
) => {
  if (!locations || !locations.length) return [];
  if (sortBy.value === 'name') {
    return sortByPropertyWithOrder<LocationInterface>(
      locations,
      'name',
      !!sortBy.order,
    );
  }
  if (sortBy.value === 'area') {
    return locations.sort((a, b) => {
      return sortBy.order
        ? getLocationWidth(b) - getLocationWidth(a)
        : getLocationWidth(a) - getLocationWidth(b);
    });
  }
  if (sortBy.value === 'lastActivity') {
    return locations.sort((a, b) => {
      return sortBy.order
        ? getLastActivityTime(b) - getLastActivityTime(a)
        : getLastActivityTime(a) - getLastActivityTime(b);
    });
  }
  return locations;
};

const getLastActivityTime = (location: LocationInterface): number =>
  location.lastActivity ? moment(location.lastActivity).valueOf() : 0;

export const getLocationWidth = (location: LocationInterface): number => {
  const area = location.area;
  if (!area) return 0;
  if (area.type === 'Point') {
    const circleShape = circle(area.coordinates, location.radius ?? 0, {
      units: 'meters',
    });
    return getLocationArea(circleShape);
  }
  return getLocationArea(area);
};

export function searchFilter<T>(
  itemsList: T[],
  search: string,
  key: string,
): T[] {
  if (search.length && itemsList.length) {
    return itemsList.filter((item) => {
      return (item as any)[key].toLowerCase().includes(search.toLowerCase());
    });
  }
  return itemsList;
}

export function filterLocationByType(
  locations: LocationInterface[],
  typeId: number | undefined,
): LocationInterface[] {
  if (!typeId || typeId === -1) {
    return locations;
  }
  return locations.filter((location) => location.type?.id === typeId);
}
